import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { AppMessage } from '@autopass-web/mochi-component';

import { useWindowDimensions } from '../../hooks/use-window-dimensions';
import { AppContext } from './app.context';

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppContextProvider = ({ children }: AppProviderProps) => {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'failure';
  } | null>(null);

  const dimensions = useWindowDimensions();
  const router = useRouter();
  const historiesRef = useRef<string[]>([]);

  const handleToastSuccess = useCallback((message: string) => {
    setToast({ message, type: 'success' });
  }, []);

  const handleToastError = useCallback((message: string) => {
    setToast({ message, type: 'failure' });
  }, []);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  useEffect(() => {
    if (historiesRef.current.length === 0) {
      historiesRef.current.push(router.asPath);
    }

    const handleRouteChange = (url: string) => {
      const histories = historiesRef.current;
      if (histories[histories.length - 1] === url) return;
      historiesRef.current.push(url);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  return (
    <AppContext.Provider
      value={{
        windowWidth: dimensions.windowWidth,
        windowHeight: dimensions.windowHeight,
        histories: historiesRef.current,
        onToastError: handleToastError,
        onToastSuccess: handleToastSuccess,
      }}
    >
      <AppMessage
        show={toast != null}
        message={toast?.message ?? ''}
        onDismiss={handleToastClose}
        type={toast?.type}
        autoDismiss
      />
      {children}
    </AppContext.Provider>
  );
};
